"use client";
import React, { Dispatch, SetStateAction, useEffect, useRef } from "react";
import Link from "next/link";
import { Config, NavLinkType } from "../../../../types";
import { FaTimes } from "react-icons/fa";
import { useFetchCompanyInfo } from "src/app/lib/fetchCompanyInfo";
import { HoverList } from "../design2/NavBar";
import {
  SocialMediaLink,
  useFetchValidSocialMediaLinks,
} from "src/app/lib/useFetcchValidSocialMediaLinks";
import useWindowWidth from "../../hooks/useWindowWidth";
import {
  FacebookIcon,
  TikTokIcon,
  TwitterIcon,
  LinkedinIcon,
  YoutubeIcon,
  InstagramIcon,
} from "../../Icon";
import { Logo } from "./components/HeroSearchComponent";
import { SocialMedia } from "../../../../types";
import { CallIcon, LocationIcon, MessageIcon } from "../../Icon";
import { useState } from "react";
import { usePathname } from "next/navigation";
import { Address } from "../../../../types";
import Button from "../../ui/Button";
import { navLinks } from "../../constants/navlink";
import Skeleton from "react-loading-skeleton";
import { getPageVisibilityClass } from "./Footer";
import Image from "next/image";
export const socialMediaIcons: Record<keyof SocialMedia, React.ReactElement> = {
  facebook: <FacebookIcon />,
  linkedin: <LinkedinIcon />,
  instagram: <InstagramIcon />,
  twitter: <TwitterIcon />,
  youtube: <YoutubeIcon />,
  tiktok: <TikTokIcon />,
};
export function getSocialIcons(socialMedia: SocialMedia, variant?: boolean) {
  return Object.entries(socialMedia).map(([platform, url]) =>
    url ? (
      <Link
        className={`${
          variant &&
          "rounded-full border border-themeColor sm:size-10 size-8 grid place-items-center"
        }`}
        key={platform}
        href={url}
        target="_blank"
        rel="noopener noreferrer"
      >
        {socialMediaIcons[platform as keyof SocialMedia]}
      </Link>
    ) : null
  );
}
export function flattenAddress(address: Address): string {
  return `${address.street}, ${address.city}, ${address.state} ${address.zipCode}, ${address.country}`;
}
const newPaths = [
  "/",
  "/properties",
  "/properties/rent",
  "/properties/sale",
  "/properties/shortlet",
];

const NavBar1 = () => {
  const breakpoint = 768;
  const width = useWindowWidth();
  const isMobile = width < breakpoint;
  const pathname = usePathname();
  const [isAtTop, setIsAtTop] = useState(false);
  const targetDivRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const { data: config } = useFetchCompanyInfo();
  const { socialMediaLinks } = useFetchValidSocialMediaLinks();
  const { data: item } = useFetchCompanyInfo();
  useEffect(() => {
    const handleScroll = () => {
      if (targetDivRef.current) {
        const { top } = targetDivRef.current.getBoundingClientRect();
        const shouldBeFixed = top <= 0 && window.scrollY > 20;
        // Only set isAtTop if pathname is NOT in newPaths
        if (!newPaths.includes(pathname)) {
          setIsAtTop(shouldBeFixed);
        } else {
          setIsAtTop(false); // Reset to false on matching paths
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll(); // Check initial position

    return () => window.removeEventListener("scroll", handleScroll);
  }, [pathname, isMobile]);

  const isPathExcluded = newPaths.includes(pathname); // Check if current path is in newPaths

  return (
    <>
      <Sidebar
        socials={socialMediaLinks}
        open={open}
        config={config}
        setOpen={setOpen}
        navLinks={navLinks}
      />
      <nav className="shadow-lg z-[300] text-primary bg-white w-full left-0 py-2">
        <div className="flex flex-col gap-5">
          {/* First part */}
          <div
            className="xlg:hidden wrapper justify-between flex"
            style={{
              minHeight: isAtTop ? targetDivRef.current?.offsetHeight : "auto",
            }}
          >
            <div className="first flex items-center gap-5">
              {item?.companyInfo?.email && (
                <div className="message flex gap-2">
                  <MessageIcon />
                  {item?.companyInfo?.email ? (
                    <Link
                      className="hover:underline"
                      href={`mailto:${item?.companyInfo?.email}`}
                    >
                      <span>{item?.companyInfo?.email}</span>
                    </Link>
                  ) : (
                    <span>No Phone Number Available</span>
                  )}
                </div>
              )}

              {item?.companyInfo?.phone_number &&
                item?.companyInfo?.phone_number.length > 0 && (
                  <div className="phone flex gap-2">
                    <CallIcon />
                    {item?.companyInfo?.phone_number[0] ? (
                      <Link
                        className="hover:underline"
                        href={`tel:${item?.companyInfo?.phone_number[0]}`}
                      >
                        <span>{item?.companyInfo?.phone_number[0]}</span>
                      </Link>
                    ) : (
                      <span>No Phone Number Available</span>
                    )}
                  </div>
                )}

              {item?.companyInfo?.head_office_address && (
                <div className="phone xl:hidden max-w-[380px] truncate items-center flex gap-2">
                  <LocationIcon />
                  {item?.companyInfo?.head_office_address ? (
                    <Link
                      href={`https://maps.google.com/?q=${encodeURIComponent(
                        item?.companyInfo.full_address ||
                          item?.companyInfo.head_office_address ||
                          ""
                      )}`}
                      className="hover:underline capitalize"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <address>{item?.companyInfo?.full_address}</address>
                    </Link>
                  ) : (
                    <span>No Address Available</span>
                  )}
                </div>
              )}
            </div>
            <div className="second items-center sm:hidden flex gap-8">
              {socialMediaLinks?.map((item, index) => (
                <Link href={item.url} key={index}>
                  {item.icon}
                </Link>
              ))}
            </div>
          </div>

          {/* Second part - Fixed only if not in newPaths */}
          <div
            ref={targetDivRef}
            className={`second  py-3 ${
              (isAtTop || isMobile) && !isPathExcluded
                ? "fixed top-0 shadow-md right-0 bg-white z-[100] w-full"
                : "relative top-0"
            }`}
          >
            <div className="flex items-center  wrapper justify-between z-[40]">
              <header className="">
                <Link className="" href="/">
                  <CompanyLogo url={item?.companyInfo?.company_logo} />
                  {/* <Logo /> */}
                </Link>
              </header>
              <NavItems config={config as Config} />
              <div className="flex items-center gap-4">
                <Button
                  href="https://app.ourproperty.com.ng/auth/user/sign-in"
                  className="xlg:hidden primary-btn"
                >
                  Sign in
                </Button>
                <Hamburger open={open} setOpen={setOpen} />
              </div>
            </div>
          </div>
        </div>
      </nav>
    </>
  );
};

export default NavBar1;

export const NavItems: React.FC<{ config: Config }> = ({ config }) => {
  const pathname = usePathname();
  // const [hovered, setHovered] = useState(false);
  const [openSub, setOpenSub] = useState(false);

  return (
    <ul className="flex xlg:hidden  gap-8 items-center">
      {navLinks.map((item, index) => {
        const isActive =
          pathname === item.link || // Exact match
          (item.link === "/properties" && pathname.startsWith("/properties/")); // Subpath match for /properties
        return (
          <li
            onMouseEnter={() => {
              // setHovered(true);
              if (item.link === "/properties") {
                setOpenSub(true);
              }
            }}
            onMouseLeave={() => {
              // setHovered(true);
              if (item.link === "/properties") {
                setOpenSub(false);
              }
            }}
            className={`group  ${getPageVisibilityClass(
              item.link,
              config,
              "!hidden"
            )} relative pb- hover:text-themeColor ${
              isActive ? "font-bold text-themeColor" : ""
            }`}
            key={index}
          >
            {openSub && item.link === "/properties" && (
              <HoverList
                className=" bg-green-500 z-[600]"
                variant={true}
                setOpen={setOpenSub}
              />
            )}
            <Link className="" href={item.link}>
              {item.name}
            </Link>
            <div className="absolute   invisible group-hover:visible size-[0.5rem] left-1/2 rounded-full bg-themeColor"></div>
          </li>
        );
      })}
    </ul>
  );
};

export const Hamburger: React.FC<{
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  className?: boolean;
}> = ({ open, setOpen }) => {
  const handleClick = () => {
    setOpen(!open);
  };

  return (
    <div
      className={"w-8 hidden xlg:block h-6 relative cursor-pointer"}
      onClick={handleClick}
    >
      {/* Top line */}
      <span
        className={`block absolute h-1 w-full bg-themeColor rounded-full transition-all duration-300 ease-in-out ${
          open ? "top-3 rotate-45" : "top-0 rotate-0"
        }`}
      ></span>

      {/* Middle line */}
      <span
        className={`block absolute h-1 w-full bg-themeColor rounded-full top-2.5 transition-all duration-300 ease-in-out ${
          open ? "opacity-0" : "opacity-100"
        }`}
      ></span>

      {/* Bottom line */}
      <span
        className={`block absolute h-1 w-full bg-themeColor rounded-full transition-all duration-300 ease-in-out ${
          open ? "top-3 -rotate-45" : "top-5 rotate-0"
        }`}
      ></span>
    </div>
  );
};

export const Sidebar: React.FC<{
  open: boolean;
  navLinks: NavLinkType[];
  setOpen: Dispatch<SetStateAction<boolean>>;
  config?: Config;
  variant?: boolean;
  design3?: boolean;
  socials: SocialMediaLink[];
}> = ({ open, setOpen, config, socials, variant }) => {
  return (
    <>
      {/* Background overlay */}
      <div
        className={`fixed z-[180] hidden xlg:block h-screen inset-0 bg-black/50 transition-opacity duration-300 ease-in-out ${
          open ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={() => setOpen(false)}
      >
        <div
          className={`z-[1000] relative h-screen min-w-[360px] max-w-[360px] xs:!min-w-0 xxs:w-[90%] bg-white transition-all duration-300 ease-in-out flex flex-col ${
            open ? "left-0" : "-left-full"
          }`}
        >
          {/* Header */}
          <div className="header mb-8 items-center px-2 py-2 h-max shadow-md w-full flex justify-between">
            <header>
              <Link href="/">
                <CompanyLogo />
              </Link>
            </header>
            <div className="text-themeColor" onClick={() => setOpen(false)}>
              <FaTimes size={20} />
            </div>
          </div>

          {/* Navigation Links */}
          <ul className="flex p-6 flex-col gap-8 items-start">
            {navLinks.map((item, index) => (
              <li
                onClick={() => setOpen(false)}
                className={`group relative pb-1 hover:text-themeColor  ${getPageVisibilityClass(
                  item.link,
                  config as Config
                )}`}
                key={index}
              >
                <Link href={item.link}>{item.name}</Link>
                <div className="absolute invisible group-hover:visible w-2 h-2 left-1/2 -translate-x-1/2 mt-1 rounded-full bg-themeColor"></div>
              </li>
            ))}
          </ul>

          {/* Bottom Section (flex pushes it down) */}
          <div className="miscelane  p-6 flex flex-col gap-4 mt-auto">
            {!variant && (
              <ul className="flex px-0 justify-between items-center">
                {socials.map((item, index) => (
                  <li
                    onClick={() => setOpen(false)}
                    className="group relative pb-1 hover:text-themeColor"
                    key={index}
                  >
                    <Link href={item.url}>{item.icon}</Link>
                    <div className="absolute invisible group-hover:visible w-2 h-2 left-1/2 -translate-x-1/2 mt-1 rounded-full bg-themeColor"></div>
                  </li>
                ))}
              </ul>
            )}

            <Button
              href="https://app.ourproperty.com.ng/auth/user/sign-in"
              className="max-w-full text-center whitespace-nowrap primary-btn w-[90%]"
            >
              Sign in
            </Button>

            {!variant && (
              <>
                {config?.companyInfo?.head_office_address && (
                  <div className="phone flex gap-2">
                    <LocationIcon />
                    <Link
                      href={`https://maps.google.com/?q=${encodeURIComponent(
                        config.companyInfo?.head_office_address
                      )}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <address>
                        {config.companyInfo?.head_office_address}
                      </address>
                    </Link>
                  </div>
                )}

                {Array.isArray(config?.companyInfo?.phone_number?.length) &&
                  config?.companyInfo?.phone_number?.length > 0 && (
                    <div className="phone flex gap-2">
                      <CallIcon />
                      <Link
                        className="hover:underline"
                        href={`tel:${config?.companyInfo?.phone_number[0]}`}
                      >
                        <span>{config?.companyInfo?.phone_number[0]}</span>
                      </Link>
                    </div>
                  )}

                {config?.companyInfo?.email && (
                  <div className="message flex gap-2">
                    <MessageIcon />
                    <Link
                      className="hover:underline"
                      href={`mailto:${config.companyInfo?.email}`}
                    >
                      <span>{config.companyInfo?.email}</span>
                    </Link>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Sidebar */}
    </>
  );
};

export const CompanyLogo: React.FC<{
  url?: string;
  className?: boolean;
  height?: string;
  width?: string;
}> = ({ className, height, width }) => {
  const { data: item, isLoading } = useFetchCompanyInfo();
  const isDemo = process.env.NEXT_PUBLIC_DEMO_MODE === "true";
  if (isDemo) {
    return <Logo width={width} height={height} />;
  }
  if (isLoading) {
    return (
      <div className={`${className}`}>
        <Skeleton height={30} width={80} />
      </div>
    );
  }
  if (item) {
    return (
      <div className="">
        <Image
          height={50}
          width={150}
          className={`max-h-[50px] ${className}`}
          src={item.companyInfo?.company_logo as string}
          alt="Company Logo"
        />
      </div>
    );
  }
  return <Logo />;
};
